import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  signInWithCustomToken,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import axios from 'axios';
import { getWalletClient } from '@wagmi/core';
import type { HorusSigner } from '../signers/types';

function getFirebaseApp() {
  if (getApps().length > 0) return getApp();
  return initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_HORUS_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_HORUS_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_HORUS_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_HORUS_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_HORUS_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_HORUS_APP_ID,
  });
}

const app = getFirebaseApp();
const storage = getStorage(app);
const auth = getAuth(app);

// ---- Non-custodial Firebase auth for storage uploads ----

let walletAuthPromise: Promise<void> | null = null;

// Module-level signer reference for re-auth without wallet popups.
// Set during XMTP initialization, used as fallback when Firebase session expires.
let storedSigner: HorusSigner | null = null;

export function setStoredSigner(signer: HorusSigner | null): void {
  storedSigner = signer;
}

/**
 * Check if Firebase auth is available without user interaction.
 * Waits for Firebase to restore an existing session from persistence.
 * Returns true if auth is available, false if a wallet signature would be needed.
 */
export async function trySilentFirebaseAuth(): Promise<boolean> {
  if (isFirebaseAuthenticated()) return true;
  return waitForAuthState();
}

/**
 * Authenticate with Firebase using an already-obtained wallet signature.
 * This avoids triggering a new wallet popup — the signature was captured
 * from the XMTP SDK's own signMessage call during Client.create().
 */
export async function authenticateWithExistingSignature(
  address: string,
  challenge: string,
  hexSignature: string,
): Promise<void> {
  if (isFirebaseAuthenticated()) return;

  const addr = address.toLowerCase();
  const apiBase = process.env.REACT_APP_SOCIAL_WALLET_API;

  const response = await axios.post(
    `${apiBase}/getChatUploadToken`,
    { walletAddress: addr, signature: hexSignature, challenge },
    {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': process.env.REACT_APP_HORUS_API_KEY || '',
      },
    }
  );

  const firebaseToken =
    response.data?.body
      ? JSON.parse(response.data.body).firebaseToken
      : response.data?.firebaseToken;

  if (!firebaseToken) {
    throw new Error('No Firebase token returned from backend');
  }

  await signInWithCustomToken(auth, firebaseToken);
}

function isFirebaseAuthenticated(): boolean {
  return auth.currentUser !== null;
}

/**
 * Wait for Firebase Auth state to be resolved (session restored from persistence).
 * On page load, Firebase restores auth sessions asynchronously. This helper
 * waits for that process to complete before checking auth state.
 */
function waitForAuthState(timeoutMs: number = 10000): Promise<boolean> {
  if (auth.currentUser) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, timeoutMs);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(timeout);
      unsubscribe();
      resolve(!!user);
    });
  });
}

/**
 * Ensure the current user has a Firebase Auth session.
 * Custodial users already have one from social login.
 * Non-custodial users get a custom token by signing a challenge with their wallet.
 *
 * Uses the stored signer (or explicit signer param) to sign a challenge,
 * avoiding a wagmi popup when possible.
 */
export async function ensureFirebaseAuth(
  walletAddress?: string,
  signer?: HorusSigner,
): Promise<void> {
  if (isFirebaseAuthenticated()) return;

  // Wait for Firebase to restore auth state from persistence (e.g. after page reload).
  // Uses 10s timeout to accommodate slow networks — this is the only reliable auth
  // path for custodial users who don't have a wagmi wallet.
  const restored = await waitForAuthState();
  if (restored) return;

  if (!walletAddress) {
    console.warn('[Firebase Auth] Auth not restored and no walletAddress for fallback signing.');
    throw new Error('Firebase auth not available');
  }

  // Deduplicate concurrent calls
  if (walletAuthPromise) return walletAuthPromise;

  // Resolve the effective signer: explicit param > stored singleton > wagmi fallback
  const effectiveSigner = signer || storedSigner;

  walletAuthPromise = (async () => {
    try {
      const address = walletAddress.toLowerCase();
      let hexSignature: string;
      let challenge: string;

      if (effectiveSigner) {
        challenge = `horus-chat-auth-${address}`;
        if (effectiveSigner.signMessageHex) {
          hexSignature = await effectiveSigner.signMessageHex(challenge);
        } else {
          const sigBytes = await effectiveSigner.signMessage(challenge);
          hexSignature = '0x' + Array.from(sigBytes).map(b => b.toString(16).padStart(2, '0')).join('');
        }
      } else {
        // Legacy fallback: use getWalletClient() directly (with retry for auto-connect timing)
        let walletClient = await getWalletClient();
        if (!walletClient) {
          await new Promise(r => setTimeout(r, 2000));
          walletClient = await getWalletClient();
        }
        if (!walletClient) {
          throw new Error('No wallet client available. Please reconnect your wallet.');
        }
        challenge = `horus-chat-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        hexSignature = await walletClient.signMessage({
          account: walletAddress as `0x${string}`,
          message: challenge,
        });
      }

      const apiBase = process.env.REACT_APP_SOCIAL_WALLET_API;

      const response = await axios.post(
        `${apiBase}/getChatUploadToken`,
        { walletAddress: address, signature: hexSignature, challenge },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_HORUS_API_KEY || '',
          },
        }
      );

      const firebaseToken =
        response.data?.body
          ? JSON.parse(response.data.body).firebaseToken
          : response.data?.firebaseToken;

      if (!firebaseToken) {
        throw new Error('No Firebase token returned from backend');
      }

      await signInWithCustomToken(auth, firebaseToken);
    } catch (err) {
      console.warn('[Firebase Auth] Auth failed:', (err as Error).message);
      throw err;
    } finally {
      walletAuthPromise = null;
    }
  })();

  return walletAuthPromise;
}

/**
 * Get the current Firebase Auth UID.
 * - Custodial users: Firebase user ID from social login
 * - Non-custodial users: wallet address (set as UID in custom token)
 *
 * Must be called after ensureFirebaseAuth().
 */
export function getFirebaseUid(): string | null {
  return auth.currentUser?.uid || null;
}

/**
 * Upload an encrypted file (Uint8Array) to Firebase Storage.
 * Returns the download URL.
 */
export async function uploadEncryptedFile(
  path: string,
  data: Uint8Array
): Promise<string> {
  const storageRef = ref(storage, path);
  const blob = new Blob([data], { type: 'application/octet-stream' });

  await uploadBytes(storageRef, blob, {
    contentType: 'application/octet-stream',
    customMetadata: {
      encrypted: 'true',
    },
  });

  return getDownloadURL(storageRef);
}

/**
 * Download an encrypted file from Firebase Storage (or any URL).
 * Returns the raw Uint8Array.
 */
export async function downloadEncryptedFile(url: string): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status}`);
  }
  const arrayBuffer = await response.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}
